// @flow
import { useMemo } from 'react';
import { useQuery } from 'react-query';
import ResidentService from '../../../../services/residentService';
import useResidentQuery from '../../../../hooks/data-fetching/useResidentQuery';
import { buildHouseholdPets } from './useHouseholdPetsQuery.helpers';

import type { QueryOptions } from 'react-query';
import type { PetViewModel } from '../types';

/** Uses shared household query keys so pet and member writes refresh both views. */
const useHouseholdPetsQuery = ({
  organizationId,
  propertyId,
  residentId,
  options = {},
}: {
  organizationId: string,
  propertyId: string,
  residentId: ?string,
  options?: QueryOptions,
}): {
  data: Array<PetViewModel>,
  isLoading: boolean,
  isFetching: boolean,
  isError: boolean,
  error: mixed,
} => {
  const residentService = useMemo(() => new ResidentService(), []);
  const queryResidentId = residentId ?? '';
  const enabled =
    Boolean(organizationId && propertyId && residentId) &&
    options.enabled !== false;

  const householdQuery = useQuery(
    ['getResidentHousehold', organizationId, propertyId, residentId],
    () =>
      residentService.getResidentHousehold(
        queryResidentId,
        organizationId,
        propertyId,
      ),
    { enabled },
  );

  const residentQuery = useResidentQuery({
    organizationId,
    propertyId,
    residentId: queryResidentId,
    options: { enabled },
  });

  const data = useMemo(
    () =>
      buildHouseholdPets({
        household: householdQuery.data,
        pendingApplicants:
          residentQuery.data?.applicants?.pendingApplicants ?? [],
        canceledApplicants:
          residentQuery.data?.applicants?.canceledApplicants ?? [],
      }),
    [householdQuery.data, residentQuery.data],
  );

  return {
    data,
    isLoading: householdQuery.isLoading || residentQuery.isLoading,
    isFetching: householdQuery.isFetching || residentQuery.isFetching,
    // Preserve error state separately from an empty pet list.
    isError: householdQuery.isError || residentQuery.isError,
    error: householdQuery.error ?? residentQuery.error,
  };
};

export default useHouseholdPetsQuery;
